import { useColorScheme } from '@mui/material/styles';
import { brandSchemes, type SchemePalette } from '@/theme/palette';

const units: Record<string, string> = { sessao: '/aula', mes: '/mês', pacote: '/pacote' };

/** "a partir de R$ 150/aula". */
export function priceLabel(cents: number | null | undefined, unit: string | null | undefined) {
  if (cents == null) return null;
  const value = (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: cents % 100 ? 2 : 0,
  });
  return `${value}${unit ? (units[unit] ?? '') : ''}`;
}

export function placeLabel(card: { city?: string | null; uf?: string | null }) {
  return card.city && card.uf ? `${card.city}, ${card.uf}` : null;
}

export function experienceLabel(years: number | null | undefined) {
  if (years == null) return null;
  if (years < 1) return 'Começou este ano';
  return years === 1 ? '1 ano de experiência' : `${years} anos de experiência`;
}

/** "há 3 horas", "em 5 dias". */
export function relativeTime(value: string | Date) {
  const diff = new Date(value).getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });
  const minutes = Math.round(diff / 60_000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  return rtf.format(Math.round(hours / 24), 'day');
}

/** Brazilian mobile/landline, formatted: (11) 98765-4321. */
export function formatPhone(digits: string | null | undefined) {
  if (!digits) return '';
  const d = digits.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return digits;
}

export function whatsappLink(digits: string, text: string) {
  return `https://wa.me/55${digits.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
}

export function citySlug(city: string) {
  return city
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function cityPath(uf: string, city: string) {
  return `/personal-em/${uf.toLowerCase()}/${citySlug(city)}`;
}

/** Pages indexed by search engines for a goal: /personal-para/emagrecimento. */
export function specialtyPath(key: string) {
  return `/personal-para/${key}`;
}

/** "Emagrecimento" → "emagrecimento", as in "personal trainer para emagrecimento". */
export function specialtyPhrase(label: string) {
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/**
 * The palette a brand color produces, from the SAME Material 3 derivation the
 * app uses for every space (so previews are the real thing). Returns plain hex
 * colors: previews paint with them directly instead of nesting MUI themes.
 */
export function schemeFor(seed: string): { light: SchemePalette; dark: SchemePalette } {
  return brandSchemes({ primary: seed });
}

export function useAccentScheme(seed: string): SchemePalette {
  const { colorScheme } = useColorScheme();
  const schemes = schemeFor(seed);
  return colorScheme === 'dark' ? schemes.dark : schemes.light;
}
